import { camelCase } from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Telemetry } from '@videoblocks/kafka-rest-client';

import { AIPromptInputRef } from '../AIPromptInput';
import { UseCase } from '../utils/constants';
import { getAutoOpenState } from '../utils/stateUtils';
import { VideoMetadata, VideoSelection } from '../utils/videoUtils';

interface VideoEditorState {
  // Video metadata and source
  videoSrc: string | null;
  videoMetadata: VideoMetadata | null;
  isLoadingMetadata: boolean;
  error: string | null;

  // Playback state
  isPlaying: boolean;
  currentTime: number;

  // Selection state
  selection: VideoSelection | null;

  // UI state
  selectedUseCase: string | null;
  defaultPlaceholder: string;
  promptText: string;
  referenceImage: string | null;
  showCreditConfirmation: boolean;
  isGeneratingTaskId: boolean;
  showPurchaseCredits: boolean;

  // Refs
  promptInputRef: React.RefObject<AIPromptInputRef>;
}

interface VideoEditorActions {
  // Video metadata actions
  setVideoSrc: (src: string | null) => void;
  setVideoMetadata: (metadata: VideoMetadata | null) => void;
  setIsLoadingMetadata: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Playback actions
  setIsPlaying: (playing: boolean) => void;
  setCurrentTime: (time: number) => void;
  handlePlayPause: () => void;

  // Selection actions
  setSelection: (selection: VideoSelection | null) => void;
  handleSelectionChange: (selection: VideoSelection) => void;
  handleTimeUpdate: (time: number) => void;

  // UI actions
  setSelectedUseCase: (useCase: string | null) => void;
  setDefaultPlaceholder: (prompt: string) => void;
  setPromptText: (prompt: string) => void;
  setReferenceImage: (image: string | null) => void;
  setShowCreditConfirmation: (show: boolean) => void;
  setIsGeneratingTaskId: (generating: boolean) => void;
  handleUseCaseSelect: (id: string, useCases: UseCase[]) => void;
  handleReferenceImageChange: (image: string | null) => void;
  setShowPurchaseCredits: (show: boolean) => void;
}

interface VideoEditorContextValue
  extends VideoEditorState,
    VideoEditorActions {}

const VideoEditorContext = createContext<VideoEditorContextValue | null>(null);

interface VideoEditorProviderProps {
  children: React.ReactNode;
  initialUseCase?: UseCase;
}

export const VideoEditorProvider: React.FC<VideoEditorProviderProps> = ({
  children,
  initialUseCase,
}) => {
  // Try to restore state from sessionStorage (SSR-safe, single operation)
  const restoredState = useMemo(() => {
    const { shouldOpen, storedState } = getAutoOpenState();
    return shouldOpen ? storedState : null;
  }, []);

  // Video metadata and source state
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [videoMetadata, setVideoMetadata] = useState<VideoMetadata | null>(
    null
  );
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  // Selection state
  const [selection, setSelection] = useState<VideoSelection | null>(
    restoredState?.selection || null
  );

  // UI state
  const [selectedUseCase, setSelectedUseCase] = useState<string | null>(
    restoredState?.selectedUseCase || initialUseCase?.id || null
  );
  const [defaultPlaceholder, setDefaultPlaceholder] = useState(
    restoredState?.defaultPlaceholder || initialUseCase?.defaultText || ''
  );
  const [promptText, setPromptText] = useState(restoredState?.promptText || '');
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const [showCreditConfirmation, setShowCreditConfirmation] = useState(false);
  const [showPurchaseCredits, setShowPurchaseCredits] = useState(false);
  const [isGeneratingTaskId, setIsGeneratingTaskId] = useState(false);

  // Refs
  const promptInputRef = useRef<AIPromptInputRef>(null);

  // Actions - memoized to prevent unnecessary re-renders
  const handlePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const handleSelectionChange = useCallback((newSelection: VideoSelection) => {
    setSelection(newSelection);
  }, []);

  const handleTimeUpdate = useCallback((time: number) => {
    setCurrentTime(time);
  }, []);

  const handleUseCaseSelect = useCallback((id: string, useCases: UseCase[]) => {
    setSelectedUseCase(id);

    const useCase = useCases.find((uc) => uc.id === id);
    if (useCase) {
      setDefaultPlaceholder(useCase.defaultText);
      promptInputRef.current?.setPromptPlaceholderValue(useCase.defaultText);
      Telemetry.increment(`ai.contentEdit.useCase.${camelCase(useCase.id)}`);
    }
  }, []);

  const handleReferenceImageChange = useCallback((image: string | null) => {
    setReferenceImage(image);
  }, []);

  const contextValue = useMemo<VideoEditorContextValue>(
    () => ({
      // State
      videoSrc,
      videoMetadata,
      isLoadingMetadata,
      error,
      isPlaying,
      currentTime,
      selection,
      selectedUseCase,
      defaultPlaceholder,
      promptText,
      referenceImage,
      showCreditConfirmation,
      isGeneratingTaskId,
      promptInputRef,
      showPurchaseCredits,

      // Actions
      setVideoSrc,
      setVideoMetadata,
      setIsLoadingMetadata,
      setError,
      setIsPlaying,
      setCurrentTime,
      handlePlayPause,
      setSelection,
      handleSelectionChange,
      handleTimeUpdate,
      setSelectedUseCase,
      setDefaultPlaceholder,
      setPromptText,
      setReferenceImage,
      setShowCreditConfirmation,
      setShowPurchaseCredits,
      setIsGeneratingTaskId,
      handleUseCaseSelect,
      handleReferenceImageChange,
    }),
    [
      videoSrc,
      videoMetadata,
      isLoadingMetadata,
      error,
      isPlaying,
      currentTime,
      selection,
      selectedUseCase,
      defaultPlaceholder,
      promptText,
      referenceImage,
      showCreditConfirmation,
      showPurchaseCredits,
      isGeneratingTaskId,
      handlePlayPause,
      handleSelectionChange,
      handleTimeUpdate,
      handleUseCaseSelect,
      handleReferenceImageChange,
    ]
  );

  return (
    <VideoEditorContext.Provider value={contextValue}>
      {children}
    </VideoEditorContext.Provider>
  );
};

// Custom hooks for consuming context
export const useVideoEditorContext = (): VideoEditorContextValue => {
  const context = useContext(VideoEditorContext);
  if (!context) {
    throw new Error(
      'useVideoEditorContext must be used within a VideoEditorProvider'
    );
  }
  return context;
};

export const useVideoSelection = () => {
  const context = useVideoEditorContext();
  return {
    selection: context.selection,
    setSelection: context.setSelection,
    handleSelectionChange: context.handleSelectionChange,
  };
};

export const useVideoPlayback = () => {
  const context = useVideoEditorContext();
  return {
    isPlaying: context.isPlaying,
    currentTime: context.currentTime,
    setIsPlaying: context.setIsPlaying,
    setCurrentTime: context.setCurrentTime,
    handlePlayPause: context.handlePlayPause,
    handleTimeUpdate: context.handleTimeUpdate,
  };
};

export const useVideoMetadata = () => {
  const context = useVideoEditorContext();
  return {
    videoSrc: context.videoSrc,
    videoMetadata: context.videoMetadata,
    isLoadingMetadata: context.isLoadingMetadata,
    error: context.error,
    setVideoSrc: context.setVideoSrc,
    setVideoMetadata: context.setVideoMetadata,
    setIsLoadingMetadata: context.setIsLoadingMetadata,
    setError: context.setError,
  };
};

export const useVideoUI = () => {
  const context = useVideoEditorContext();
  return {
    selectedUseCase: context.selectedUseCase,
    defaultPlaceholder: context.defaultPlaceholder,
    promptText: context.promptText,
    referenceImage: context.referenceImage,
    showCreditConfirmation: context.showCreditConfirmation,
    isGeneratingTaskId: context.isGeneratingTaskId,
    promptInputRef: context.promptInputRef,
    setSelectedUseCase: context.setSelectedUseCase,
    setDefaultPlaceholder: context.setDefaultPlaceholder,
    setPromptText: context.setPromptText,
    setReferenceImage: context.setReferenceImage,
    setShowCreditConfirmation: context.setShowCreditConfirmation,
    setIsGeneratingTaskId: context.setIsGeneratingTaskId,
    handleUseCaseSelect: context.handleUseCaseSelect,
    handleReferenceImageChange: context.handleReferenceImageChange,
    showPurchaseCredits: context.showPurchaseCredits,
    setShowPurchaseCredits: context.setShowPurchaseCredits,
  };
};
